import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import type { VictoriaLogsQueryResult } from './types';

export interface VictoriaLogsApi {
  query(options: {
    entityRef: string;
    start: string;
    end: string;
    limit?: number;
  }): Promise<VictoriaLogsQueryResult>;
}

export const victoriaLogsApiRef = createApiRef<VictoriaLogsApi>().with({
  id: 'plugin.victorialogs.service',
});

export class VictoriaLogsClient implements VictoriaLogsApi {
  readonly #discoveryApi: DiscoveryApi;
  readonly #fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.#discoveryApi = options.discoveryApi;
    this.#fetchApi = options.fetchApi;
  }

  async query(options: {
    entityRef: string;
    start: string;
    end: string;
    limit?: number;
  }): Promise<VictoriaLogsQueryResult> {
    const baseUrl = await this.#discoveryApi.getBaseUrl('victorialogs');
    const url = new URL(`${baseUrl}/logs`);
    url.searchParams.set('entityRef', options.entityRef);
    url.searchParams.set('start', options.start);
    url.searchParams.set('end', options.end);
    url.searchParams.set('limit', String(options.limit ?? 200));
    const response = await this.#fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(
        response.status === 404
          ? 'This entity has no VictoriaLogs query configured.'
          : 'Unable to query VictoriaLogs.',
      );
    }
    return response.json() as Promise<VictoriaLogsQueryResult>;
  }
}

export const victoriaLogsApi = ApiBlueprint.make({
  name: 'service',
  params: defineParams =>
    defineParams({
      api: victoriaLogsApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new VictoriaLogsClient({ discoveryApi, fetchApi }),
    }),
});
